import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'

import { EmailFormValues } from '@/enums/forms'
import { emailFormFieldsKeys } from '@/utils/formsUtils'

import { ButtonSpinner, PrimaryButton } from '@/components/buttons'
import { CustomInput } from '@/components/input'
import { InputErrorMessage } from '@/components/inputErrorMessage'
import { PageTitle } from '@/components/pageTitle'
import { TextHTML } from '@/components/typography'

import { emailFormValidationSchema } from '@/validation/emailVerificationFormSchema'

import { TEmailFormValues } from '../types'

export type TEmailFormProps = {
  email?: string | null
  title: string
  formDescription: string | ((email: string) => string)
  buttonName: string
  isLoading: boolean
  inputPlaceholder: string
  errorMessage?: string
  onSubmit: SubmitHandler<TEmailFormValues>
}
export const EmailForm = ({
  email,
  title,
  formDescription,
  buttonName,
  isLoading,
  inputPlaceholder,
  errorMessage,
  onSubmit,
}: TEmailFormProps) => {
  let preparedFormDescription: string | undefined
  if (formDescription && typeof formDescription === 'function') {
    preparedFormDescription = formDescription(email || 'your email')
  } else if (formDescription) {
    preparedFormDescription = formDescription
  }

  const [isFocus, setFocus] = useState(false)

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
    watch,
  } = useForm<TEmailFormValues>({
    resolver: yupResolver(emailFormValidationSchema),
    mode: 'all',
  })

  const emailField = watch(EmailFormValues.email)
  const [emailValue] = getValues(emailFormFieldsKeys)

  useEffect(() => {
    if (!emailField?.length && email) {
      setValue('email', email)
    }
  }, [])

  const handleFocus = async () => {
    setFocus((prevState) => !prevState)
    if (emailValue?.length) {
      await trigger(EmailFormValues.email)
    }
  }

  return (
    <>
      <PageTitle title={title} />
      {preparedFormDescription && (
        <TextHTML className="page-description" title={preparedFormDescription} />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={'email'}
          control={control}
          render={({ field: { ref } }) => {
            const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
              if (errors.email) {
                clearErrors(EmailFormValues.email)
              }
              event?.target?.value && setValue(EmailFormValues.email, event?.target?.value)
            }

            return (
              <CustomInput
                name={'email'}
                type={'email'}
                autoComplete={'email'}
                ref={ref}
                isError={Boolean(errors?.email?.message)}
                isFocus={isFocus}
                onChange={handleChange}
                defaultValue={email || ''}
                placeholder={inputPlaceholder}
                spellCheck={false}
                onFocus={handleFocus}
                onBlur={handleFocus}
                isValid={
                  !Boolean(errors?.email?.message) && !isFocus && Boolean(emailValue?.length)
                }
                readOnly={isLoading}
                disabled={isLoading}
              />
            )
          }}
        />
        <InputErrorMessage
          rowNumber={1}
          message={errors?.email?.message ? errors.email.message : errorMessage}
        />
        <div className="button-container">
          <PrimaryButton
            type="submit"
            disabled={isLoading || !emailField?.length || Boolean(errors?.email?.message)}
          >
            {isLoading ? <ButtonSpinner /> : buttonName}
          </PrimaryButton>
        </div>
      </form>
    </>
  )
}
